import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  Divider,
  Button,
  MenuItem
} from "@mui/material";

import { auth, db } from "../../../firebase";
import Swal from "sweetalert2";

import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  Timestamp,
  orderBy
} from "firebase/firestore";

function StudentInfo({ form, handleChange, schoolId }) {
  const [siblings, setSiblings] = useState([
    { name: "", gender: "", grade: "", school: "" }
  ]);

  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [studenttypes, setStudenttypes] = useState([]);
  const [locations, setlocations] = useState([]);

  // 🔥 Detect Boarding
  const isBoarding = (form.studenttype || "").toLowerCase().includes("boarding");

  useEffect(() => {
    if (form.siblings) {
      setSiblings(form.siblings);
    }
  }, [form.siblings]);

  // 🔥 Fetch master data
  useEffect(() => {
    if (!schoolId) return;

    const fetchData = async (collectionName, fieldName, setState) => {
      const q = query(
        collection(db, collectionName),
        where("schoolId", "==", schoolId)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({
        id: doc.id,
        name: doc.data()[fieldName]
      }));

      setState(list);
    };

    const fetchDataOrdered = async (collectionName, fieldName, setState) => {
      const q = query(
        collection(db, collectionName),
        where("schoolId", "==", schoolId),
        orderBy("sequence", "asc")
      );

      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({
        id: doc.id,
        name: doc.data()[fieldName]
      }));

      setState(list);
    };

    const loadMasters = async () => {
      try {
        await fetchData("religions", "name", setReligions);
        await fetchData("castes", "type", setCastes);
        await fetchData("mediums", "type", setMediums);
        await fetchData("universities", "univName", setUniversities);
        await fetchDataOrdered("courses", "name", setCourses);
        await fetchData("studenttypes", "type", setStudenttypes);
        await fetchData("locations", "locationName", setlocations);
      } catch (err) {
        console.error("Master data error", err);
      }
    };

    loadMasters();
  }, [schoolId]);

  // 🔥 Handle form change with validation and transport reset logic
  const handleFieldChange = (field, value) => {
    // 🔥 VALIDATION: Allow ONLY alphabets and spaces for Names
    if (["firstname", "fathername", "surname"].includes(field)) {
      if (value !== "" && !/^[a-zA-Z\s]*$/.test(value)) {
        return; // Prevent typing numbers or special characters
      }
    }

    let updated = { ...form, [field]: value };

    // 👉 If Boarding selected → clear transport
    if (field === "studenttype" && value.toLowerCase().includes("boarding")) {
      updated.transport = "";
    }

    handleChange(field, value);
    
    if (field === "studenttype") {
      handleChange("transport", updated.transport || "");
    }
  };

  const handleSiblingChange = (index, field, value) => {
    const updated = [...siblings];
    updated[index][field] = value;
    setSiblings(updated);
    handleChange("siblings", updated);
  };

  const addSibling = () => {
    const updated = [...siblings, { name: "", gender: "", grade: "", school: "" }];
    setSiblings(updated);
    handleChange("siblings", updated);
  };

  const removeSibling = (index) => {
    const updated = siblings.filter((_, i) => i !== index);
    setSiblings(updated);
    handleChange("siblings", updated);
  };

  // 🔥 Save
  const handleSave = async () => {
    if (!schoolId) {
      Swal.fire("Error", "School ID missing", "error");
      return;
    }

    // Manual validation check before saving
    if (!form.firstname || !form.surname || !form.fathername) {
      Swal.fire("Error", "Please fill out Surname, First Name, and Father's Name", "error");
      return;
    }

    try {
      const studentId = form.id;

      const studentData = {
        ...form,
        rollno: form.rollno || "",
        class: form.class || "",
        schoolId,
        updatedAt: Timestamp.now(),
        createdAt: form.createdAt || Timestamp.now(),
        status: "Active",
        userid: auth.currentUser?.uid,
      };

      await setDoc(doc(db, "students", studentId), studentData, { merge: true });

      await setDoc(
        doc(db, "admission", studentId),
        {
          studentId,
          schoolId,
          rollno: form.rollno || "",
          class: form.class || "",
          lastupdatedBy: auth.currentUser?.uid,
          updatedAt: Timestamp.now()
        },
        { merge: true }
      );

      Swal.fire("Success", "Student Saved Successfully", "success");
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const fields = [
    { label: "Roll No.", name: "rollno" },
    { label: "Admission No", name: "admno" },
    { label: "GR No", name: "grno" },
    { label: "Surname *", name: "surname", required: true },
    { label: "First Name *", name: "firstname", required: true },
    { label: "Father's Name *", name: "fathername", required: true },
    { label: "Mother's Name", name: "mothername" },
    {
      label: "Gender", name: "gender", type: "select", options: [
        { id: "M", name: "MALE" },
        { id: "F", name: "FEMALE" }
      ]
    },
    { label: "Date of Birth", name: "dob", type: "date" },
    { label: "Place of Birth", name: "placeofbirth" },
    { label: "Religion", name: "religion", type: "select", options: religions },
    { label: "Nationality", name: "nationality" },
    { label: "Caste", name: "caste", type: "select", options: castes },
    { label: "Medium", name: "medium", type: "select", options: mediums },
    { label: "Board", name: "university", type: "select", options: universities },

    {
      label: "Student Type",
      name: "studenttype",
      type: "select",
      options: studenttypes
    },

    {
      label: "Transport Location",
      name: "transport",
      type: "select",
      options: locations,
      disabled: isBoarding // 🔥 MAIN LOGIC
    },

    { label: "Adhar No.", name: "adhar" },
    { label: "Sub-Caste", name: "subcaste" },
    { label: "Student Portal No.", name: "portal" },
    { label: "Language", name: "language" },
    { label: "Previous School ", name: "lastschool" },
    { label: "Previous School Mobile", name: "lastschoolmobile" },
    { label: "PEN No", name: "penno" },
    { label: "Saral No", name: "saralno" },
    { label: "School Udise No", name: "schooludiseno" }
  ];

  return (
    <Box display="flex" justifyContent="center" bgcolor="#f4f6f8">
      <Paper sx={{ p: 3, width: "100%" }}>
        <Box sx={{ background: "#0e6363", color: "#fff", p: 1, mb: 3 }}>
          <Typography fontWeight="bold">1) Student Information</Typography>
        </Box>

        <Grid spacing={2} className="row">
          {/* Class */}
          <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Class"
              size="small"
              value={form.class || ""}
              onChange={(e) => handleFieldChange("class", e.target.value)}
            >
              {courses?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {fields.map((field, idx) => (
            <Grid className="col-sm-3 mb-3" item xs={12} sm={3} key={idx}>
              <TextField
                select={field.type === "select"}
                fullWidth
                label={field.label}
                size="small"
                disabled={field.disabled}
                required={field.required}
                type={field.type === "date" ? "date" : "text"}
                InputLabelProps={field.type === "date" ? { shrink: true } : {}}
                value={form[field.name] || ""}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                error={field.required && form[field.name] === ""}
              >
                {field.type === "select" &&
                  field.options?.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={field.name === "transport" ? option.id : option.name}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Siblings */}
        <Box sx={{ background: "#0e6363", color: "#fff", p: 1, mb: 3 }}>
          <Typography fontWeight="bold">2) Sibling Information</Typography>
        </Box>

        {siblings.map((sib, index) => (
          <Grid container spacing={2} key={index}>
            <Grid className="col-sm-3 mb-3" item xs={3}>
              <TextField fullWidth size="small" label="Name"
                value={sib.name}
                onChange={(e) => handleSiblingChange(index, "name", e.target.value)}
              />
            </Grid>
            <Grid className="col-sm-2 mb-3" item xs={2}>
              {/* 🔥 SIBLING GENDER CHANGED TO SELECT BOX */}
              <TextField 
                select
                fullWidth 
                size="small" 
                label="Gender"
                value={sib.gender || ""}
                onChange={(e) => handleSiblingChange(index, "gender", e.target.value)}
              >
                <MenuItem value="MALE">MALE</MenuItem>
                <MenuItem value="FEMALE">FEMALE</MenuItem>
              </TextField>
            </Grid>
            <Grid className="col-sm-2 mb-3" item xs={2}>
              <TextField fullWidth size="small" label="Grade"
                value={sib.grade}
                onChange={(e) => handleSiblingChange(index, "grade", e.target.value)}
              />
            </Grid>
            <Grid className="col-sm-2 mb-3" item xs={3}>
              <TextField fullWidth size="small" label="School"
                value={sib.school}
                onChange={(e) => handleSiblingChange(index, "school", e.target.value)}
              />
            </Grid>
            <Grid className="col-sm-1 mb-3" item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button color="error" onClick={() => removeSibling(index)}>Remove</Button>
            </Grid>
          </Grid>
        ))}

        <Box mt={2}>
          <Button variant="outlined" onClick={addSibling}>+ Add Sibling</Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default StudentInfo;